import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { URLS } from "config/urls";
import { CloseIcon } from "icons";
import { colors } from "theme/palette";
import { handleInvalidRequest } from "utils/sdk";
import { addQueryParams, reverse } from "utils/urls";

import Drawer from "components/Drawer";
import IconButton from "components/IconButton";
import SidepanelHeader from "components/SidepanelHeader";
import Stack from "components/Stack";
import Text from "components/Text";
import { useConfirmationDialog } from "components/useConfirmationDialog";

import { MemoSnapshotCreationMethod } from "entities/Memo/components/Tabs/constants";
import { memoSignatureProcessCancel } from "entities/Memo/sdk";

import MemoESignatureContentAssignmentOverview from "./components/MemoESignatureContentAssignmentOverview";
import MemoESignatureNotificationEmail from "./components/MemoESignatureNotificationEmail";
import MemoESignatureStepper from "./components/MemoESignatureStepper";
import MemoESignatureDrawerWarningMessageContent from "./components/MemoESignatureWarningMessageDrawerContent";
import MemoESignaturePDFView from "./components/MemoSignaturePDFView";
import { MemoESignatureDrawerSteps } from "./constants";

export interface IMemoESignatureDrawer {
  isOpen: boolean;
  onClose: () => void;
  memoName: string;
  companyName: string;
  isProcessRestarted: boolean;
  snapshotCreationMethod?: MemoSnapshotCreationMethod;
}

const MemoESignatureDrawer = ({
  isOpen,
  onClose,
  memoName,
  companyName,
  isProcessRestarted,
  snapshotCreationMethod,
}: IMemoESignatureDrawer) => {
  const params = useParams<{
    dealId: string;
    memoId: string;
  }>();
  const dealId = Number(params.dealId);
  const memoId = Number(params.memoId);

  const [currentDrawerState, setCurrentDrawerStep] =
    useState<MemoESignatureDrawerSteps>(
      MemoESignatureDrawerSteps.WARNING_MESSAGE
    );

  const navigate = useNavigate();

  const { show: showConfirmationDialog } = useConfirmationDialog();

  const confirmationDialogOnClose = useCallback(() => {
    showConfirmationDialog({
      onConfirm: () =>
        memoSignatureProcessCancel({ dealId, memoId })
          .then(onClose)
          .catch(handleInvalidRequest),
      confirmButtonText: "Close",
      message:
        "Closing this will result in losing your progress. Are you sure you want to proceed?",
    });
  }, [showConfirmationDialog, dealId, memoId, onClose]);

  const setDrawerStepToContactAssignmentOverview = useCallback(
    () => setCurrentDrawerStep(MemoESignatureDrawerSteps.ROLES_AND_CONTACTS),
    []
  );

  const setDrawerStepToNotificationEmail = useCallback(
    () => setCurrentDrawerStep(MemoESignatureDrawerSteps.NOTIFICATION_EMAIL),
    []
  );

  const setDrawerStepPDFReview = useCallback(
    () => setCurrentDrawerStep(MemoESignatureDrawerSteps.REVIEW_AND_SEND),
    []
  );

  const navigateToPrintableMemoPage = useCallback(
    () =>
      navigate(
        addQueryParams(
          reverse(URLS.PRINTABLE_DEAL_MEMO, {
            dealId,
            memoId,
          }),
          { creationMethod: MemoSnapshotCreationMethod.E_SIGN_PROCESS }
        )
      ),
    [dealId, memoId, navigate]
  );

  useEffect(() => {
    if (isOpen) {
      if (isProcessRestarted) {
        setDrawerStepToContactAssignmentOverview();
      } else if (
        snapshotCreationMethod === MemoSnapshotCreationMethod.E_SIGN_PROCESS
      ) {
        setDrawerStepPDFReview();
      } else {
        setCurrentDrawerStep(MemoESignatureDrawerSteps.WARNING_MESSAGE);
      }
    }
  }, [
    isOpen,
    snapshotCreationMethod,
    setDrawerStepPDFReview,
    isProcessRestarted,
    setDrawerStepToContactAssignmentOverview,
  ]);

  return (
    <Drawer
      open={isOpen}
      anchor="right"
      sx={{
        width: "1014px",
        "& .MuiDrawer-paper": {
          width: "1014px",
          display: "flex",
        },
      }}
      onClose={
        currentDrawerState === MemoESignatureDrawerSteps.REVIEW_AND_SEND
          ? confirmationDialogOnClose
          : onClose
      }
    >
      <Stack>
        <SidepanelHeader>
          <Stack>
            <Text variant="label" color={colors.blue100}>
              memos | {memoName}
            </Text>
            <Text variant="h1">Start e-sign process</Text>
          </Stack>
          <IconButton
            size="small"
            onClick={
              currentDrawerState === MemoESignatureDrawerSteps.REVIEW_AND_SEND
                ? confirmationDialogOnClose
                : onClose
            }
            sx={{ alignSelf: "flex-start" }}
          >
            <CloseIcon />
          </IconButton>
        </SidepanelHeader>
        {currentDrawerState !== MemoESignatureDrawerSteps.WARNING_MESSAGE && (
          <MemoESignatureStepper currentStep={currentDrawerState} />
        )}
      </Stack>

      {currentDrawerState === MemoESignatureDrawerSteps.WARNING_MESSAGE && (
        <MemoESignatureDrawerWarningMessageContent
          onClose={onClose}
          setDrawerStepToContactAssignmentOverview={
            setDrawerStepToContactAssignmentOverview
          }
        />
      )}
      {currentDrawerState === MemoESignatureDrawerSteps.ROLES_AND_CONTACTS && (
        <MemoESignatureContentAssignmentOverview
          setDrawerStepToNotificationEmail={setDrawerStepToNotificationEmail}
        />
      )}

      {currentDrawerState === MemoESignatureDrawerSteps.NOTIFICATION_EMAIL && (
        <MemoESignatureNotificationEmail
          navigateToPrintableMemoPage={navigateToPrintableMemoPage}
          companyName={companyName}
          memoName={memoName}
        />
      )}
      {currentDrawerState === MemoESignatureDrawerSteps.REVIEW_AND_SEND && (
        <MemoESignaturePDFView onClose={onClose} />
      )}
    </Drawer>
  );
};

export default MemoESignatureDrawer;
